/*
 * Recalculate tab: request builders and the recalc_status -> view model mapping. I/O-free; the
 * shapes mirror goserver month_recalc_all.go (RecalcAllResponse / RecalcAllStatusResponse).
 */
import { api_pay_month } from '../additional_payments/params';
import { fmt, fmt_count } from '../format';

export const POLL_MS = 30000;
export const RUN_ALREADY_RUNNING = 'already_running';
export const RUN_FAILED_TEXT = 'The run could not recalculate this teacher.';

// POST /month/recalc_all and GET /month/recalc_status both take pay_month as YYYY-MM-01.
export const recalc_all_payload = (pay_month) => ({ pay_month: api_pay_month(pay_month) });
export const recalc_status_params = (pay_month) => ({ pay_month: api_pay_month(pay_month) });

// The 202 answer: a held lock is not a failure, the tab just polls the run in progress.
export const start_outcome = (data) => {
  const d = data || {};
  return {
    already_running: d.run === RUN_ALREADY_RUNNING,
    message: d.message || ''
  };
};

const plural = (n, one, many) => `${fmt_count(n)} ${Number(n) === 1 ? one : many}`;

// Every issue the status carries, in one list: compute errors first, then run failures not already
// listed (run_failed_teachers, else the bare uuids), then the run's own last_error (no teacher).
export const status_issues = (s) => {
  const errs = Array.isArray(s.compute_errors) ? s.compute_errors : [];
  const issues = errs.map((e) => ({
    teacher_uuid: e.teacher_uuid || '',
    teacher_name: e.teacher_name || '',
    error: e.error || ''
  }));
  const listed = new Set(issues.map((i) => i.teacher_uuid));
  const failed =
    Array.isArray(s.run_failed_teachers) && s.run_failed_teachers.length
      ? s.run_failed_teachers.map((t) => ({
          teacher_uuid: (t && t.teacher_uuid) || '',
          teacher_name: (t && t.teacher_name) || ''
        }))
      : (Array.isArray(s.run_failed_teacher_uuids) ? s.run_failed_teacher_uuids : []).map((u) => ({
          teacher_uuid: u || '',
          teacher_name: ''
        }));
  failed.forEach((t) => {
    if (t.teacher_uuid && !listed.has(t.teacher_uuid)) {
      listed.add(t.teacher_uuid);
      issues.push({ ...t, error: RUN_FAILED_TEXT });
    }
  });
  if (s.last_error) issues.push({ teacher_uuid: '', teacher_name: '', error: s.last_error });
  return issues;
};

// The name worth showing beside the uuid ('' when missing or the server's uuid fallback).
export const shown_teacher_name = (issue) => {
  const uuid = (issue && issue.teacher_uuid) || '';
  const name = (issue && issue.teacher_name) || '';
  return name && name !== uuid ? name : '';
};

// "Name — full uuid"; the server already falls back to the uuid as the name, so don't repeat it.
export const issue_teacher_label = (issue) => {
  const uuid = (issue && issue.teacher_uuid) || '';
  const name = (issue && issue.teacher_name) || '';
  if (!uuid) return '';
  if (!name || name === uuid) return uuid;
  return `${name} — ${uuid}`;
};

// phase: 'running' | 'error' (any issue) | 'done' (a run finished clean) | 'idle' (no run recorded).
export const status_view = (status) => {
  if (!status) return null;
  const s = status;
  const issues = status_issues(s);
  const running = s.running === true;
  let phase = 'idle';
  if (running) phase = 'running';
  else if (issues.length) phase = 'error';
  else if (s.started_at_ist || s.finished_at_ist) phase = 'done';
  const waiting = Number(s.waiting) || 0;
  return {
    phase,
    running,
    open: Number(s.open) || 0,
    done: Number(s.done) || 0,
    waiting,
    progress_text: `${fmt_count(s.done || 0)} of ${plural(s.open || 0, 'open month', 'open months')} done, ${fmt_count(waiting)} waiting`,
    started_at: s.started_at_ist || '',
    finished_at: s.finished_at_ist || '',
    run_recomputed: Number(s.run_recomputed) || 0,
    done_text: `Done — ${plural(s.run_recomputed || 0, 'month', 'months')} recalculated`,
    // a clean finish can still leave months flagged (outside the hourly window, or flagged after the run).
    waiting_note:
      phase === 'done' && waiting > 0
        ? `${plural(waiting, 'month is', 'months are')} still waiting; the hourly run picks them up, or Recalculate again.`
        : '',
    issues,
    truncated: s.compute_errors_truncated === true
  };
};

// Poll again only while the server says the run is going.
export const should_poll = (status) => Boolean(status && status.running === true);

/*
 * "Recalculate selected teachers": a chip list, then one POST /recalc per teacher, in order. Rows are
 * queued -> running -> done | skipped | error, or cancelled by Stop before they start.
 */
export const MAX_SELECTED = 50;
export const ROW = {
  QUEUED: 'queued',
  RUNNING: 'running',
  DONE: 'done',
  SKIPPED: 'skipped',
  ERROR: 'error',
  CANCELLED: 'cancelled'
};

// A lookup row -> a chip; duplicates and anything past the cap are refused with a note.
export const add_selected = (chips, teacher) => {
  const list = Array.isArray(chips) ? chips : [];
  const uuid = (teacher && teacher.teacher_uuid) || '';
  if (!uuid) return { chips: list, note: '' };
  if (list.some((c) => c.teacher_uuid === uuid)) {
    return { chips: list, note: `${teacher.name || uuid} is already in the list.` };
  }
  if (list.length >= MAX_SELECTED) {
    return { chips: list, note: `At most ${MAX_SELECTED} teachers at a time.` };
  }
  return { chips: [...list, { teacher_uuid: uuid, teacher_name: teacher.name || '' }], note: '' };
};

export const remove_selected = (chips, teacher_uuid) =>
  (Array.isArray(chips) ? chips : []).filter((c) => c.teacher_uuid !== teacher_uuid);

export const start_rows = (chips) =>
  (Array.isArray(chips) ? chips : []).map((c) => ({
    teacher_uuid: c.teacher_uuid,
    teacher_name: c.teacher_name || '',
    state: ROW.QUEUED
  }));

// The next row to run, or -1 when none is queued.
export const next_queued = (rows) => rows.findIndex((r) => r.state === ROW.QUEUED);

const patch = (rows, i, fields) => rows.map((r, n) => (n === i ? { ...r, ...fields } : r));

export const mark_running = (rows, i) => patch(rows, i, { state: ROW.RUNNING });

// Payable in rupees: payable_paise when the server sent it, else net (the same figure before payouts).
const payable_of = (totals) => {
  if (!totals || typeof totals !== 'object') return null;
  if (typeof totals.payable_paise === 'number') return totals.payable_paise / 100;
  const n = Number(totals.net);
  return totals.net === null || totals.net === undefined || !Number.isFinite(n) ? null : n;
};

// A /recalc 200 -> the row's outcome; recompute.ok false is an error even on a 200.
export const mark_result = (rows, i, data) => {
  const r = (data && data.recompute) || {};
  if (r.ok === false) {
    return patch(rows, i, { state: ROW.ERROR, error: r.error || 'The recalculation failed.' });
  }
  if (r.skipped) return patch(rows, i, { state: ROW.SKIPPED, skip_reason: r.skip_reason || '' });
  return patch(rows, i, {
    state: ROW.DONE,
    before: payable_of(r.totals_before),
    after: payable_of(r.totals_after)
  });
};

export const mark_error = (rows, i, text) =>
  patch(rows, i, { state: ROW.ERROR, error: text || 'The recalculation failed.' });

// Stop: the running row finishes; everything still queued is cancelled.
export const cancel_queued = (rows) =>
  rows.map((r) => (r.state === ROW.QUEUED ? { ...r, state: ROW.CANCELLED } : r));

export const rows_active = (rows) =>
  (Array.isArray(rows) ? rows : []).some((r) => r.state === ROW.QUEUED || r.state === ROW.RUNNING);

const STATE_TEXT = {
  [ROW.QUEUED]: 'Queued',
  [ROW.RUNNING]: 'Running…',
  [ROW.DONE]: 'Done',
  [ROW.SKIPPED]: 'Skipped',
  [ROW.ERROR]: 'Error',
  [ROW.CANCELLED]: 'Cancelled'
};

export const result_row_view = (row) => {
  let detail = '';
  if (row.state === ROW.DONE) {
    detail =
      row.before !== null && row.after !== null
        ? `Payable ${fmt(row.before, 2)} → ${fmt(row.after, 2)}`
        : 'Recalculated';
  } else if (row.state === ROW.SKIPPED) detail = row.skip_reason || 'Not recalculated';
  else if (row.state === ROW.ERROR) detail = row.error || '';
  return {
    key: row.teacher_uuid,
    teacher: issue_teacher_label(row),
    teacher_uuid: row.teacher_uuid,
    teacher_name: shown_teacher_name(row),
    state: row.state,
    state_text: STATE_TEXT[row.state] || row.state,
    detail
  };
};

// One line under the rows once nothing is queued or running.
export const rows_summary = (rows) => {
  const count = (s) => rows.filter((r) => r.state === s).length;
  const parts = [
    [ROW.DONE, 'done'],
    [ROW.SKIPPED, 'skipped'],
    [ROW.ERROR, 'failed'],
    [ROW.CANCELLED, 'cancelled']
  ]
    .map(([s, word]) => (count(s) ? `${fmt_count(count(s))} ${word}` : ''))
    .filter(Boolean);
  return parts.join(', ');
};

// Why the start button is off, or '' when it may start.
export const selected_block_reason = ({ chips, month_running, busy }) => {
  if (busy) return '';
  if (month_running) return 'A month-wide recalculation is running; wait for it to finish.';
  if (!chips || !chips.length) return 'Add at least one teacher.';
  return '';
};

export const selected_button_text = (n, month_label) =>
  Number(n) > 0
    ? `Recalculate ${fmt_count(n)} ${Number(n) === 1 ? 'teacher' : 'teachers'} for ${month_label}`
    : `Recalculate selected teachers for ${month_label}`;
